import { acceptedFileTypes, DropzoneErrorMessage, maxFiles, maxFileSize } from '@/constants/general';
import { ErrorMsg, FileWithPath } from '@/types/common';
import useLanguage from '@/utils/hooks/useLanguage';
import { useSso } from '@/utils/providers/SSOProvider';
import { Dropzone } from '@mantine/dropzone';
import cn from 'classnames';
import React, { Dispatch, Fragment, SetStateAction, useEffect } from 'react';
import { MdClose, MdOutlineFileUpload, MdOutlineImage } from 'react-icons/md';
import { DocumentPreview } from './DocumentPreview';

export type RequestFileProofUploadProps = {
    hasFile?: boolean;
    previewFile?: string;
    files: FileWithPath[];
    setFiles: Dispatch<React.SetStateAction<FileWithPath[]>>;
    setError: Dispatch<SetStateAction<ErrorMsg>>;
    requestStatus?: any;
    // requestStatus?: Request['requestStatus'];
    fileType?: string;
    requestDetails?: {
        entityId: number;
        requestId: number;
    };
};

export const RequestFileProofUpload: React.FC<RequestFileProofUploadProps> = ({
    setError,
    previewFile,
    files,
    hasFile,
    setFiles,
    requestStatus,
    fileType,
    requestDetails
}) => {
    const noFilesUploaded = !previewFile && !hasFile && !files.length;

    const lang = useLanguage();
    const { canRequestDeposit, canRequestWithdrawal } = useSso();

    useEffect(() => {
        if (files.length > 0) {
            setError({ message: '' });
        }
    }, [files]);

    return (
        <Fragment>
            {noFilesUploaded && (
                <Fragment>
                    {requestStatus !== 'INITIATED' && requestStatus !== undefined && (
                        <div className="items-center">There is no proof of ownership</div>
                    )}
                    {(requestStatus === 'INITIATED' || requestStatus === undefined) &&
                        (canRequestDeposit || canRequestWithdrawal) && (
                            <Dropzone
                                name="proofOfOwnership"
                                className="flex items-center justify-center h-full w-full bg-neutral-background py-2 px-4 rounded-sm border border-dashed border-neutral-500 cursor-pointer"
                                onDrop={setFiles}
                                autoFocus={true}
                                onReject={(error) =>
                                    setError({ message: DropzoneErrorMessage[error[0].errors[0].code] })
                                }
                                maxFiles={maxFiles}
                                maxSize={maxFileSize}
                                accept={acceptedFileTypes}>
                                <div className={cn('h-full w-full flex items-center justify-center text-center')}>
                                    {!files.length && (
                                        <div className="flex flex-col text-center items-center gap-3 text-sm text-brand-text">
                                            <Dropzone.Accept>
                                                <MdOutlineFileUpload className="w-10 h-10" />
                                            </Dropzone.Accept>
                                            <Dropzone.Idle>
                                                <MdOutlineImage className="w-10 h-10" />
                                            </Dropzone.Idle>
                                            <Dropzone.Reject>
                                                <MdClose className="w-10 h-10" />
                                            </Dropzone.Reject>
                                            <div className="text-brand-text">{lang.portal.dragOrClickToUpload}</div>
                                            <div className="text-sm text-neutral-500 flex flex-col">
                                                <span>
                                                    {lang.portal.supportFileFormatsAre}{' '}
                                                    <b>.jpeg, .png, .pdf, .mp4, .avi, .webm, .mpg</b>
                                                </span>
                                                <span>
                                                    {lang.portal.attachedFileShouldNotExceed} <b>10mb</b>
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </Dropzone>
                        )}
                </Fragment>
            )}

            {!noFilesUploaded && (
                <DocumentPreview
                    files={files}
                    setFiles={setFiles}
                    previewFile={previewFile}
                    fileType={fileType}
                    requestDetails={requestDetails}
                />
            )}
        </Fragment>
    );
};
